import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Autocomplete,
  TextField,
} from "@mui/material";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchFolders, moveDocumentToFolder } from "../../api/documentApi";
import toast from "react-hot-toast";

const MoveDocumentModal = ({ open, onClose, documentId, currentFolder }) => {
  const queryClient = useQueryClient();
  const [selectedFolder, setSelectedFolder] = useState(currentFolder || null);

  const { data: folderData, isLoading } = useQuery({
    queryKey: ["folders"],
    queryFn: fetchFolders,
    enabled: open,
  });

  const folders = Array.isArray(folderData?.data) ? folderData.data : (folderData?.data?.folders || []);

  const moveMutation = useMutation({
    mutationFn: moveDocumentToFolder,
    onSuccess: (res) => {
      if (res.status) {
        toast.success(res.message || "Document moved successfully!");
        queryClient.invalidateQueries(["documents"]);
        onClose();
        setSelectedFolder(null);
      } else {
        toast.error(res.message || "Failed to move document");
      }
    },
    onError: (err) => {
      const errMsg = err.response?.data?.error || err.response?.data?.message || err.message || "An error occurred";
      toast.error(errMsg);
    },
  });

  const handleMove = () => {
    if (!selectedFolder) {
      toast.error("Please select a folder");
      return;
    }
    moveMutation.mutate({
      document_upload_id: documentId,
      folder_id: selectedFolder.id,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: "16px", py: 1.5 }}>Set Folder</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mt: 1 }}>
          <Autocomplete
            options={folders}
            getOptionLabel={(option) => option.name || ""}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            value={selectedFolder}
            onChange={(event, newValue) => setSelectedFolder(newValue)}
            loading={isLoading}
            renderInput={(params) => (
              <TextField {...params} label="Select Folder" variant="outlined" size="small" fullWidth />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button onClick={handleMove} variant="contained" disabled={moveMutation.isLoading} sx={{ backgroundColor: "#4A148C" }}>
          {moveMutation.isLoading ? "Moving..." : "Move"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MoveDocumentModal;
